import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { BandKm } from "../api/types";
import { CONDITION_COLOR } from "./Badges";
import { Icon, type IconName } from "./Icon";
import { EmptyState } from "./States";

export function KpiTile({ label, value, note, tone, to, bar, icon }: {
  label: string; value: ReactNode; note?: ReactNode; tone?: "good" | "fair" | "poor" | "attention"; to?: string;
  bar?: { value: number; color: string }; icon?: IconName;
}) {
  const text = (
    <>
      <span className="kpi-label">{icon ? <Icon name={icon} size={16} /> : null}{label}</span>
      <span className="kpi-value">{value}</span>
      {bar ? <span className="bar" aria-hidden="true"><span style={{ width: `${Math.min(bar.value, 100)}%`, background: bar.color }} /></span> : null}
      {note ? <span className="kpi-note">{note}</span> : null}
    </>
  );
  const cls = `kpi${tone ? ` ${tone}` : ""}`;
  return to ? <Link to={to} className={cls}>{text}</Link> : <div className={cls}>{text}</div>;
}

const BANDS: { key: keyof Pick<BandKm, "good" | "fair" | "poor" | "not_rated">; label: string }[] = [
  { key: "good", label: "Good" }, { key: "fair", label: "Fair" }, { key: "poor", label: "Poor" }, { key: "not_rated", label: "Not rated" },
];

/** Stacked km by condition per group (agency, county, surface). Bars share one scale. */
export function BandBars({ rows, empty }: { rows: BandKm[]; empty: string }) {
  if (!rows.length) return <EmptyState title={empty} />;
  const max = Math.max(...rows.map((r) => r.total), 0.001);
  return (
    <div>
      {rows.map((r) => (
        <div className="hbar-row" key={r.name}>
          <span className="hbar-name" title={r.name}>{r.name}</span>
          <span className="bar" style={{ width: `${(r.total / max) * 100}%` }}
                aria-label={BANDS.map((b) => `${b.label} ${r[b.key].toFixed(1)} km`).join(", ")}>
            {BANDS.map((b) => r[b.key] > 0 ? (
              <span key={b.key} title={`${b.label}: ${r[b.key].toFixed(1)} km`}
                    style={{ width: `${(r[b.key] / r.total) * 100}%`, background: CONDITION_COLOR[b.label] }} />
            ) : null)}
          </span>
          <span className="val">{r.total.toFixed(1)} km</span>
        </div>
      ))}
      <div className="legend" style={{ marginTop: 8 }}>
        {BANDS.map((b) => <span key={b.key}><i style={{ background: CONDITION_COLOR[b.label] }} />{b.label}</span>)}
      </div>
    </div>
  );
}

/** Verified vs unreviewed counts per type. */
export function CountBars({ rows, empty }: { rows: { type_name: string; verified: number; unreviewed: number }[]; empty: string }) {
  if (!rows.length) return <EmptyState title={empty} />;
  const max = Math.max(...rows.map((r) => r.verified + r.unreviewed), 1);
  return (
    <div>
      {rows.map((r) => (
        <div className="hbar-row" key={r.type_name}>
          <span className="hbar-name" title={r.type_name}>{r.type_name}</span>
          <span className="bar" style={{ width: `${((r.verified + r.unreviewed) / max) * 100}%`, minWidth: 4 }}
                aria-label={`${r.verified} verified, ${r.unreviewed} unreviewed`}>
            {r.verified ? <span style={{ width: `${(r.verified / (r.verified + r.unreviewed)) * 100}%`, background: "var(--navy)" }} /> : null}
            {r.unreviewed ? <span style={{ width: `${(r.unreviewed / (r.verified + r.unreviewed)) * 100}%`, background: "var(--fair-soft)", outline: "1px dashed var(--fair)", outlineOffset: -1 }} /> : null}
          </span>
          <span className="val">{r.verified}{r.unreviewed ? <span className="muted"> +{r.unreviewed}</span> : null}</span>
        </div>
      ))}
      <div className="legend" style={{ marginTop: 8 }}>
        <span><i style={{ background: "var(--navy)" }} />Verified</span>
        <span><i style={{ background: "var(--fair-soft)", outline: "1px dashed var(--fair)" }} />Unreviewed (not counted)</span>
      </div>
    </div>
  );
}
